import React from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';

export interface HeightSelectorProps {
  /** Current height, formatted like 5'8" (or undefined/empty if not set). */
  value?: string | null;
  /** Fires with a value formatted like 5'8" — the single canonical stored format. */
  onChange: (height: string) => void;
  onBlur?: () => void;
  hasError?: boolean;
  disabled?: boolean;
}

const FEET_OPTIONS = [4, 5, 6, 7];
const INCH_OPTIONS = Array.from({ length: 12 }, (_, i) => i); // 0-11

// Parses "5'8"" / "5'8" / "5' 8"" into { feet, inches }; returns nulls if unparseable.
function parseHeight(value?: string | null): { feet: number | null; inches: number | null } {
  if (!value) return { feet: null, inches: null };
  const match = value.match(/^(\d+)'\s*(\d+)"?$/);
  if (!match) return { feet: null, inches: null };
  return { feet: parseInt(match[1], 10), inches: parseInt(match[2], 10) };
}

export const HeightSelector: React.FC<HeightSelectorProps> = ({
  value,
  onChange,
  onBlur,
  hasError,
  disabled,
}) => {
  const { feet, inches } = parseHeight(value);

  const emit = (nextFeet: number, nextInches: number) => {
    onChange(`${nextFeet}'${nextInches}"`);
  };

  const handleFeetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    emit(parseInt(e.target.value, 10), inches ?? 0);
  };

  const handleInchesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    emit(feet ?? 5, parseInt(e.target.value, 10));
  };

  return (
    <HeightRow>
      <HeightSelect aria-label="Height — feet" value={feet ?? ''} onChange={handleFeetChange} onBlur={onBlur} $hasError={hasError} disabled={disabled}>
        <option value="" disabled>Feet</option>
        {FEET_OPTIONS.map((ft) => (
          <option key={ft} value={ft}>{ft} ft</option>
        ))}
      </HeightSelect>

      <HeightSelect aria-label="Height — inches" value={inches ?? ''} onChange={handleInchesChange} onBlur={onBlur} $hasError={hasError} disabled={disabled}>
        <option value="" disabled>Inches</option>
        {INCH_OPTIONS.map((inch) => (
          <option key={inch} value={inch}>{inch} in</option>
        ))}
      </HeightSelect>
    </HeightRow>
  );
};

const HeightRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.sm};
`;

const HeightSelect = styled.select<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.75rem;
  background: ${theme.colors.background.card};
  border: 1px solid ${props => props.$hasError ? theme.colors.error : theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.$hasError ? `${theme.colors.error}20` : `${theme.colors.primary}20`};
  }

  option {
    background: ${theme.colors.background.card};
    color: ${theme.colors.text.primary};
  }
`;
